import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import {
  IRhiV2ErrorCopy,
  RHI_V2_ERROR_COPY,
  RHI_V2_GENERIC_ERROR,
  RHI_V2_NETWORK_ERROR,
} from '@/constants/rhiV2ErrorCopy';
import { RHI_V2_ERROR_CODES } from '@/constants/healthInsuranceConfig';
import { IRhiV2CommitConflict, IRhiV2ErrorBody } from '@/types/rhiV2';

// ---------- Error codes and copy ----------

/**
 * The one read of a failed request. `cause` is set only on PROPOSAL_REVALIDATION_FAILED, where the
 * pay tap reports why; `effectiveCode` is that cause, else the code.
 */
const rhiV2ErrorOf = (error: unknown) => {
  const response = (error as AxiosError<IRhiV2ErrorBody>)?.response;
  if (!response) return undefined;
  const body = response.data;
  const code = body?.error?.code;
  const cause =
    code === RHI_V2_ERROR_CODES.PROPOSAL_REVALIDATION_FAILED
      ? body?.error?.cause
      : undefined;
  return {
    status: response.status,
    body,
    code,
    cause,
    effectiveCode: cause ?? code,
    meta: body?.error?.meta,
  };
};

const hasCode = (error: unknown, code: string) => {
  const parsed = rhiV2ErrorOf(error);
  return parsed?.code === code || parsed?.cause === code;
};

export const rhiV2ErrorStatus = (error: unknown) => rhiV2ErrorOf(error)?.status;

export const rhiV2ErrorBody = (error: unknown) => rhiV2ErrorOf(error)?.body;

export const rhiV2ErrorCode = (error: unknown) => rhiV2ErrorOf(error)?.code;

export const rhiV2ErrorMessage = (error: unknown) =>
  rhiV2ErrorBody(error)?.error?.message;

/** Refused answers: top level on a page save, under error.meta on affirm and submit. */
export const rhiV2AnswerErrors = (error: unknown) => {
  const body = rhiV2ErrorBody(error);
  return body?.errors ?? body?.error?.meta?.errors ?? [];
};

/** The version a failed request moved the proposal to: top level on a 422, under error.meta otherwise. */
export const rhiV2ErrorVersion = (error: unknown) => {
  const parsed = rhiV2ErrorOf(error);
  const version =
    parsed?.status === 422 ? parsed.body?.version : parsed?.meta?.version;
  return typeof version === 'number' ? version : undefined;
};

/**
 * Why the pay tap's PROPOSAL_REVALIDATION_FAILED happened. On POST /payment/initiate it sits flat
 * on `error`, not under `meta` (guide 2026-10-14: the earlier guides were wrong).
 */
export const rhiV2RevalidationCause = (error: unknown) =>
  rhiV2ErrorOf(error)?.cause;

/**
 * The insurer couldn't price the plan just now (guide 2026-10-14 §2): keep the screen and offer
 * Try again. The pay tap sends it as `error.cause`.
 */
export const isRhiV2InsurerPriceFailure = (error: unknown) =>
  ([
    RHI_V2_ERROR_CODES.INSURER_PRICE_FAILED,
    RHI_V2_ERROR_CODES.RISKCOVRY_REQUEST_FAILED,
  ] as string[]).includes(rhiV2ErrorOf(error)?.effectiveCode ?? '');

// ---------- Plan ids (guide 2026-10-14 §2) ----------

const PLAN_LIST_CODES: string[] = [
  RHI_V2_ERROR_CODES.PLAN_LIST_CHANGED,
  RHI_V2_ERROR_CODES.PLAN_NOT_IN_QUOTE,
];

const PLAN_GONE_CODES: string[] = [
  RHI_V2_ERROR_CODES.PLAN_NO_LONGER_OFFERED,
  RHI_V2_ERROR_CODES.PLAN_NOT_SELECTABLE,
];

/** The saved add-ons can't be priced as they stand; the plan page is where they change. */
const ADDON_CODES: string[] = [
  RHI_V2_ERROR_CODES.ADDON_OPTION_REQUIRED,
  RHI_V2_ERROR_CODES.ADDON_OPTION_INVALID,
  RHI_V2_ERROR_CODES.ADDON_COMBINATION_NOT_ALLOWED,
  RHI_V2_ERROR_CODES.ADDON_NOT_APPLIED,
];

export type TRhiV2PlanRefusal =
  | { kind: 'replaced'; planId?: number }
  | { kind: 'updating'; retryAfterMs?: number }
  | { kind: 'reloadList' }
  | { kind: 'gone'; code: string }
  | { kind: 'addons'; code: string };

/**
 * A plan id that can't be used, or a saved selection that can't be priced, from any plan-scoped
 * route. The pay tap sends the code as `error.cause` inside PROPOSAL_REVALIDATION_FAILED.
 */
export const rhiV2PlanRefusal = (
  error: unknown,
): TRhiV2PlanRefusal | undefined => {
  const parsed = rhiV2ErrorOf(error);
  const code = parsed?.effectiveCode;
  const meta = parsed?.meta as
    | { planId?: number; retryAfterMs?: number }
    | undefined;
  if (!code) return undefined;

  if (code === RHI_V2_ERROR_CODES.PLAN_ID_REPLACED) {
    return { kind: 'replaced', planId: meta?.planId };
  }
  if (code === RHI_V2_ERROR_CODES.PLAN_LIST_UPDATING) {
    return { kind: 'updating', retryAfterMs: meta?.retryAfterMs };
  }
  if (PLAN_LIST_CODES.includes(code)) return { kind: 'reloadList' };
  if (PLAN_GONE_CODES.includes(code)) return { kind: 'gone', code };
  if (ADDON_CODES.includes(code)) return { kind: 'addons', code };
  return undefined;
};

/** Affirm and submit refuse a file that must be uploaded again; payment reports it as the cause. */
export const isRhiV2DocumentStale = (error: unknown) =>
  hasCode(error, RHI_V2_ERROR_CODES.DOCUMENT_STALE);

/** Affirm and submit refuse saved answers that disagree with KYC; payment reports it as the cause. */
export const isRhiV2IdentityMismatch = (error: unknown) =>
  hasCode(error, RHI_V2_ERROR_CODES.KYC_IDENTITY_MISMATCH);

/**
 * Affirm, submit or a payment re-submit found the form incomplete or an answer that isn't one
 * of its choices. Nothing reached the insurer, and a retry can't pass: the customer has to
 * go back to the form. Payment reports it as the cause of a revalidation failure.
 */
export const rhiV2ProposalRefusal = (error: unknown) => {
  const parsed = rhiV2ErrorOf(error);
  const refusal =
    parsed?.code === RHI_V2_ERROR_CODES.PROPOSAL_REVALIDATION_FAILED
      ? parsed.cause
      : parsed?.code;
  return refusal === RHI_V2_ERROR_CODES.PROPOSAL_INCOMPLETE ||
    refusal === RHI_V2_ERROR_CODES.ANSWER_INVALID
    ? refusal
    : undefined;
};

/**
 * The questions the customer confirmed have changed since (e.g. the insurer dropped one), so the
 * proposal must be confirmed again. Payment reports it as the cause of a revalidation failure.
 */
export const isRhiV2ProposalNotAffirmed = (error: unknown) =>
  hasCode(error, RHI_V2_ERROR_CODES.PROPOSAL_NOT_AFFIRMED);

/** Our proposal version is behind the server's: re-read the form before anything else. */
export const isRhiV2VersionStale = (code: string | undefined) =>
  code === RHI_V2_ERROR_CODES.VERSION_CONFLICT ||
  code === RHI_V2_ERROR_CODES.FORM_VERSION_CHANGED;

export const rhiV2IdentityMismatches = (error: unknown) =>
  rhiV2ErrorOf(error)?.meta?.mismatches ?? [];

export const rhiV2StaleDocumentKeys = (error: unknown) =>
  (rhiV2ErrorOf(error)?.meta?.stale ?? []).map(
    (item) => item.questionKey,
  );

export const rhiV2CommitConflict = (error: unknown) =>
  (error as AxiosError<IRhiV2CommitConflict>)?.response?.data;

/**
 * An add-on whose choices the insurer erased before the 15 Oct fix (`ADDON_OPTION_INVALID` with
 * `meta.choices: []`): every price on the plan is refused and nothing the customer changes can
 * help, so the way on is a fresh quote (guide 2026-10-15 §8).
 */
export const isRhiV2QuoteUnpriceable = (error: unknown) => {
  const parsed = rhiV2ErrorOf(error);
  if (parsed?.code !== RHI_V2_ERROR_CODES.ADDON_OPTION_INVALID) return false;
  const { choices } = (parsed.meta ?? {}) as { choices?: unknown };
  return Array.isArray(choices) && choices.length === 0;
};

/** Over 7 days old: the journey is over unless meta.payable says it can still be paid. Never retried. */
export const isRhiV2QuoteExpired = (error: unknown) => {
  const parsed = rhiV2ErrorOf(error);
  return (
    parsed?.status === 409 && parsed.code === RHI_V2_ERROR_CODES.QUOTE_EXPIRED
  );
};

/**
 * Nothing on this quote can change, but its journey card knows where to go: paid
 * (QUOTE_LOCKED), or expired while still payable. Re-read the list and route the card.
 */
export const isRhiV2QuoteLocked = (error: unknown) => {
  const parsed = rhiV2ErrorOf(error);
  return (
    parsed?.status === 409 &&
    (parsed.code === RHI_V2_ERROR_CODES.QUOTE_LOCKED ||
      (parsed.code === RHI_V2_ERROR_CODES.QUOTE_EXPIRED &&
        parsed.meta?.payable === true))
  );
};

export type ResolvedRhiV2Error = IRhiV2ErrorCopy & { code?: string };

export const resolveRhiV2Error = (error: unknown): ResolvedRhiV2Error => {
  const code = rhiV2ErrorCode(error);
  const mapped = code ? RHI_V2_ERROR_COPY[code] : undefined;

  if (mapped) return { code, ...mapped };

  if (code) {
    Sentry.captureMessage('rhi-v2 error code has no copy', {
      level: 'warning',
      extra: { code, status: rhiV2ErrorStatus(error) },
    });
  }

  return { code, ...RHI_V2_GENERIC_ERROR };
};

export const rhiV2CodeCopy = (code: string | undefined, fallback: string) => {
  const copy = code ? RHI_V2_ERROR_COPY[code] : undefined;
  return copy ? [copy.title, copy.body].filter(Boolean).join('. ') : fallback;
};

export const rhiV2ToastCopy = (error: unknown, fallback: string) =>
  rhiV2CodeCopy(rhiV2ErrorCode(error), fallback);

export const toastRhiV2Error = (
  error: unknown,
  fallback: string,
  id: string,
) => toast.error(rhiV2ToastCopy(error, fallback), { id });

// ---------- Blocked proposal screens ----------

export const PROPOSAL_NETWORK_ERROR = 'NETWORK_UNAVAILABLE';

export const PROPOSAL_BLOCKING_CODES: string[] = [
  RHI_V2_ERROR_CODES.COMPOSITION_CHANGED,
  RHI_V2_ERROR_CODES.FORM_UNAVAILABLE,
  RHI_V2_ERROR_CODES.PROPOSAL_FROZEN,
];

export interface IProposalBlockedActions {
  choosePlan: () => void;
  retry: () => void;
}

export interface IProposalBlockedCopy {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}

const errorCopy = (code: string) => {
  const { title, body = '' } = RHI_V2_ERROR_COPY[code];
  return { title, body };
};

export const proposalBlockedCopy = (
  code: string | undefined,
  { choosePlan, retry }: IProposalBlockedActions,
): IProposalBlockedCopy | undefined => {
  if (!code) return undefined;

  return {
    [RHI_V2_ERROR_CODES.COMPOSITION_CHANGED]: {
      ...errorCopy(RHI_V2_ERROR_CODES.COMPOSITION_CHANGED),
      actionLabel: 'Choose a plan',
      onAction: choosePlan,
    },
    [RHI_V2_ERROR_CODES.FORM_UNAVAILABLE]: {
      ...errorCopy(RHI_V2_ERROR_CODES.FORM_UNAVAILABLE),
      actionLabel: 'Try again',
      onAction: retry,
    },
    [PROPOSAL_NETWORK_ERROR]: {
      ...RHI_V2_NETWORK_ERROR,
      actionLabel: 'Try again',
      onAction: retry,
    },
    [RHI_V2_ERROR_CODES.PROPOSAL_FROZEN]: {
      ...errorCopy(RHI_V2_ERROR_CODES.PROPOSAL_FROZEN),
      actionLabel: 'Try again',
      onAction: retry,
    },
  }[code];
};
